import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Phone, PhoneOff, Video, VideoOff } from "lucide-react";
import { Avatar } from "@/components/chat/Avatar";

function MediaView({ stream, muted, className, testid }) {
    const ref = useRef(null);
    useEffect(() => { if (ref.current) ref.current.srcObject = stream || null; }, [stream]);
    return <video ref={ref} autoPlay playsInline muted={muted} className={className} data-testid={testid} />;
}

function useTimer(startedAt) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => { if (!startedAt) return undefined; const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, [startedAt]);
    if (!startedAt) return "";
    const s = Math.max(0, Math.floor((now - startedAt) / 1000));
    return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

const STATE_LABEL = { ringing: "Ringing…", incoming: "Incoming call", connecting: "Connecting…" };

function RoundBtn({ onClick, tone = "bg-white/15 hover:bg-white/25", children, testid, label }) {
    return (
        <button onClick={onClick} aria-label={label} className={`w-14 h-14 rounded-full flex items-center justify-center text-white transition-colors ${tone}`} data-testid={testid}>
            {children}
        </button>
    );
}

export function CallOverlay({ call, localStream, remoteStream, onAccept, onDecline, onHangup }) {
    const [micOn, setMicOn] = useState(true);
    const [camOn, setCamOn] = useState(true);
    const timer = useTimer(call.startedAt);
    const video = call.kind === "video";
    const toggle = (kind, on, set) => { localStream?.getTracks().filter((t) => t.kind === kind).forEach((t) => { t.enabled = !on; }); set(!on); };

    return (
        <div className="fixed inset-0 z-[100] bg-[#0B1226] text-white flex flex-col animate-fade-in" data-testid="call-overlay">
            {video && remoteStream && <MediaView stream={remoteStream} className="absolute inset-0 w-full h-full object-cover" testid="call-remote-video" />}
            {!video && <MediaView stream={remoteStream} className="hidden" testid="call-remote-audio" />}
            <div className="relative z-10 flex-1 flex flex-col items-center justify-center gap-4 p-6 text-center">
                {!(video && remoteStream) && <Avatar name={call.peer.name} size="xl" />}
                <div className="font-display font-bold text-2xl sm:text-3xl drop-shadow" data-testid="call-peer-name">{call.peer.name}</div>
                <div className="text-sm text-white/70" data-testid="call-state">
                    {call.state === "active" ? timer : STATE_LABEL[call.state]} · {video ? "Video call" : "Voice call"}
                </div>
            </div>
            {video && localStream && (
                <MediaView stream={localStream} muted className="absolute z-20 right-4 top-4 w-28 sm:w-44 aspect-[3/4] object-cover rounded-2xl border border-white/20 shadow-2xl" testid="call-local-video" />
            )}
            <div className="relative z-10 pb-10 pt-4 flex items-center justify-center gap-5">
                {call.state === "incoming" ? (
                    <>
                        <RoundBtn onClick={onDecline} tone="bg-red-600 hover:bg-red-700" testid="call-decline-btn" label="Decline"><PhoneOff className="w-6 h-6" /></RoundBtn>
                        <RoundBtn onClick={onAccept} tone="bg-emerald-600 hover:bg-emerald-700" testid="call-accept-btn" label="Accept">
                            {video ? <Video className="w-6 h-6" /> : <Phone className="w-6 h-6" />}
                        </RoundBtn>
                    </>
                ) : (
                    <>
                        <RoundBtn onClick={() => toggle("audio", micOn, setMicOn)} testid="call-mute-btn" label="Mute">{micOn ? <Mic className="w-6 h-6" /> : <MicOff className="w-6 h-6" />}</RoundBtn>
                        {video && <RoundBtn onClick={() => toggle("video", camOn, setCamOn)} testid="call-camera-btn" label="Camera">{camOn ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}</RoundBtn>}
                        <RoundBtn onClick={onHangup} tone="bg-red-600 hover:bg-red-700" testid="call-hangup-btn" label="Hang up"><PhoneOff className="w-6 h-6" /></RoundBtn>
                    </>
                )}
            </div>
        </div>
    );
}
